/**
 * RFC 0063: Collapsible Code Block Component
 *
 * Wraps code blocks with expand/collapse functionality.
 * Auto-collapses blocks > 10 lines for better chat readability.
 */

import { useState, useMemo } from 'react'

interface CollapsibleCodeBlockProps {
  children: React.ReactNode
  language?: string
  collapseThreshold?: number
}

export function CollapsibleCodeBlock({
  children,
  language,
  collapseThreshold = 10,
}: CollapsibleCodeBlockProps) {
  const [isCollapsed, setIsCollapsed] = useState<boolean | null>(null)
  const [copied, setCopied] = useState(false)

  // Extract text content for line counting and copying
  const textContent = useMemo(() => {
    if (typeof children === 'string') return children
    // Handle nested code element from react-markdown
    const codeElement = children as React.ReactElement
    if (codeElement?.props?.children) {
      return String(codeElement.props.children)
    }
    return ''
  }, [children])

  const lineCount = textContent.split('\n').length
  const shouldAutoCollapse = lineCount > collapseThreshold

  // Determine actual collapsed state (user override or auto)
  const collapsed = isCollapsed ?? shouldAutoCollapse

  // Preview: first 3 lines when collapsed
  const previewLines = textContent.split('\n').slice(0, 3).join('\n')
  const hasMore = lineCount > 3

  const handleCopy = async () => {
    await navigator.clipboard.writeText(textContent)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="my-2 rounded-lg bg-panel border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-card border-b border-border">
        <button
          onClick={() => setIsCollapsed(!collapsed)}
          className="flex items-center gap-1.5 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
        >
          {collapsed ? (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
          <span className="font-mono font-normal">{language || 'code'}</span>
          <span className="font-normal text-text-muted">({lineCount} lines)</span>
        </button>

        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs font-medium text-text-muted hover:text-text-secondary transition-colors"
          title="Copy code"
        >
          {copied ? (
            <>
              <svg className="w-3.5 h-3.5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-success">Copied</span>
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code content */}
      <div className="overflow-x-auto">
        <pre className="p-3 text-sm whitespace-pre-wrap break-words m-0">
          {collapsed ? (
            <>
              <code className="text-primary font-mono font-normal">{previewLines}</code>
              {hasMore && (
                <span className="font-normal text-text-muted italic">
                  {'\n'}... {lineCount - 3} more lines
                </span>
              )}
            </>
          ) : (
            <code className="text-primary font-mono font-normal">{textContent}</code>
          )}
        </pre>
      </div>

      {/* Expand button when collapsed */}
      {collapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          className="w-full py-1.5 text-xs font-medium text-text-muted hover:text-text-secondary hover:bg-card-hover transition-colors border-t border-border"
        >
          Show all {lineCount} lines
        </button>
      )}
    </div>
  )
}
